import React, { useState } from 'react';
import { X, Mail, AlertTriangle } from 'lucide-react';
import { callEdgeFunction } from '../../utils/supabaseFunctions';
import { supabase } from '../../services/supabaseClient';

interface ShareStrategyDialogProps {
  strategyId: string;
  onClose: () => void;
  onShared: () => void;
}

const ShareStrategyDialog: React.FC<ShareStrategyDialogProps> = ({
  strategyId,
  onClose,
  onShared,
}) => {
  const [email, setEmail] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [canDuplicate, setCanDuplicate] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleShare = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    try {
      setSharing(true);
      setError(null);

      // Since we no longer have the strategies table, we'll use a direct RPC call
      // to share the template instead
      const { data: _shareData, error: shareError } = await supabase.rpc(
        'share_template',
        {
          template_id: strategyId,
          email: email.trim(),
          can_edit: canEdit,
          can_duplicate: canDuplicate,
        }
      );

      if (shareError) throw shareError;

      // Send email notification
      const response = await callEdgeFunction('notify-share', {
        templateId: strategyId,
        recipientEmail: email.trim(),
        canEdit,
        canDuplicate,
      });

      if (!response.ok) {
        throw new Error('Failed to send email notification');
      }

      onShared();
      onClose();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Failed to share strategy'
      );
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-800">Share Strategy</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Share with
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="canEdit"
                  checked={canEdit}
                  onChange={(e) => setCanEdit(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="canEdit" className="ml-2 text-sm text-gray-700">
                  Allow editing
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="canDuplicate"
                  checked={canDuplicate}
                  onChange={(e) => setCanDuplicate(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="canDuplicate"
                  className="ml-2 text-sm text-gray-700"
                >
                  Allow duplicating
                </label>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              onClick={handleShare}
              disabled={sharing}
              className={`w-full flex items-center justify-center px-4 py-2 rounded-md text-white font-medium transition-colors ${
                sharing
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {sharing ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sharing...
                </>
              ) : (
                'Share Strategy'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareStrategyDialog;
